const ALLOWED_HTTP_PROTOCOLS = new Set(['http:', 'https:']);

function parseHttpUrl(value: string, baseUrl?: string): URL | null {
  try {
    const url = baseUrl === undefined ? new URL(value) : new URL(value, baseUrl);

    if (
      !ALLOWED_HTTP_PROTOCOLS.has(url.protocol) ||
      url.username !== '' ||
      url.password !== ''
    ) {
      return null;
    }

    return url;
  } catch {
    return null;
  }
}

/**
 * Accepts an administrator-configured, absolute HTTP(S) image URL.
 * Credentials and active/non-network schemes are deliberately rejected.
 */
export function toExternalImageUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  return parseHttpUrl(value)?.href ?? null;
}

/**
 * Resolves an API-owned image path without allowing it to change the configured
 * API origin. Relative upload paths and absolute URLs on that origin are valid.
 */
export function toApiImageUrl(
  apiEndpoint: string,
  resourcePath: unknown,
  browserBaseUrl: string = document.baseURI
): string | null {
  if (typeof resourcePath !== 'string' || resourcePath.trim() === '') {
    return null;
  }

  const endpoint = parseHttpUrl(apiEndpoint, browserBaseUrl);
  if (endpoint === null) {
    return null;
  }

  const endpointBase = `${endpoint.href.replace(/\/+$/, '')}/`;
  const endpointPath = endpoint.pathname.replace(/\/+$/, '') || '/';
  const resourceIsAbsolute = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(resourcePath);
  const resourceIsNetworkPath = resourcePath.startsWith('//');
  const rootRelativeAlreadyUsesApiBase =
    resourcePath === endpointPath || resourcePath.startsWith(`${endpointPath}/`);
  const candidate = parseHttpUrl(
    resourceIsAbsolute || resourceIsNetworkPath || rootRelativeAlreadyUsesApiBase
      ? resourcePath
      : resourcePath.replace(/^\/+/, ''),
    rootRelativeAlreadyUsesApiBase ? endpoint.origin : endpointBase
  );

  if (
    candidate === null ||
    candidate.origin !== endpoint.origin ||
    (endpointPath !== '/' &&
      candidate.pathname !== endpointPath &&
      !candidate.pathname.startsWith(`${endpointPath}/`))
  ) {
    return null;
  }

  return candidate.href;
}

/**
 * Creates a browser-owned URL only for Blob values that can reasonably be an
 * image. Empty and generic binary MIME types are retained for legacy camera
 * endpoints whose generated clients do not expose a precise response type.
 */
export function createImageObjectUrl(value: unknown): string | null {
  if (!(value instanceof Blob)) {
    return null;
  }

  if (
    value.type !== '' &&
    value.type !== 'application/octet-stream' &&
    !value.type.toLowerCase().startsWith('image/')
  ) {
    return null;
  }

  return URL.createObjectURL(value);
}

export function revokeImageObjectUrl(value: string | null): void {
  if (value !== null) {
    URL.revokeObjectURL(value);
  }
}
